import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import clsx from "clsx";
import rg4js from "raygun4js";
import { useEffect, useMemo, useRef, useState } from "react";
import { CloseButton, OverlayTrigger, Tooltip } from "react-bootstrap";
import { InfoCircle } from "react-bootstrap-icons";
import { useParams } from "react-router-dom";
import {
  type CaseFileLite,
  type CaseFilePage,
  DocumentStatus,
  DocumentSubType,
  Source,
} from "@/briefpoint-client";
import { CaseFileType } from "@/briefpoint-client/models/CaseFileType";
import { useCaseApi, useCaseFileApi } from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import { getCaseOptions } from "@/lib/query-options";
import {
  getSubTypeText,
  normalizeError,
  sanitizeConvertableFiles,
  uploadFilesToAzure,
} from "@/lib/utils";
import Button from "../Button";
import LeavingGuard from "../LeavingGuard";
import Loading from "../Loading";
import { toast } from "../ui/toast";
import styles from "./ComplaintStep.module.scss";
import StepIndicator from "./StepIndicator";

interface Props {
  hasWarning?: boolean;
  isComplete: boolean;
  setIsComplete: (isComplete: boolean) => void;
}

export default function ComplaintStep(props: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { id: caseIdParam } = useParams<{ id: string }>();
  const [estimatedProgress, setEstimatedProgress] = useState(0);
  const caseFileApi = useCaseFileApi();
  const [hasUploadedFiles, setHasUploadedFiles] = useState(false);
  const queryClient = useQueryClient();
  const caseApi = useCaseApi();
  const { firm } = useAuth();

  const caseQuery = useQuery({
    ...getCaseOptions(caseApi, firm?.id, caseIdParam),
    enabled: !!firm?.id && !!caseIdParam,
  });

  const caseFilesQueryOptions = queryOptions({
    queryKey: [
      "caseFileApi.caseFileGetCaseFiles",
      {
        caseId: caseIdParam,
        fileType: CaseFileType.Pleading,
        pageSize: 1,
      },
    ],
    queryFn: () =>
      caseFileApi.caseFileGetCaseFiles({
        caseId: caseIdParam,
        fileType: CaseFileType.Pleading,
        pageSize: 1,
      }),
    select: (data) => data.items?.[0],
  });

  const caseFilesQuery = useQuery(caseFilesQueryOptions);

  useEffect(() => {
    props.setIsComplete(!!caseFilesQuery.data?.id);
  }, [caseFilesQuery.data?.id, props.setIsComplete]);

  const uploadFileMutation = useMutation({
    mutationFn: async (files: File[]) => {
      const { data } = await uploadFilesToAzure({
        files,
        caseId: caseIdParam,
        type: CaseFileType.Pleading,
        caseFileApi,
      });
      return data;
    },
    onSuccess: async () => {
      // Fake progress to 50%
      setEstimatedProgress(50);
      // Trigger the polling query to check on the status of the azure upload.
      setHasUploadedFiles(true);
    },
    onError: (error) => {
      rg4js("send", {
        error: normalizeError(error),
      });
      toast.error("Unable to upload complaint file.");
    },
  });

  const uploadBatchesQueryKey = useMemo(
    () => [
      "caseFileApi.caseFileGetCaseFileUploadBatches",
      "ComplaintStep",
      { caseId: caseIdParam },
    ],
    [caseIdParam],
  );

  // Check on the status of the azure upload.
  const uploadBatchesQuery = useQuery({
    queryKey: uploadBatchesQueryKey,
    queryFn: () =>
      hasUploadedFiles
        ? caseFileApi.caseFileGetCaseFileUploadBatches({ caseId: caseIdParam })
        : // Hack to stop multiple requests when the component is re-mounted.
          Promise.resolve([]),
    enabled: hasUploadedFiles,
    retry: 1,
    retryDelay: 500,
    refetchInterval: (query) => {
      // When there are no documents in the batch, they have been uploaded. Stop polling.
      if (!query || query.state.data?.length === 0) {
        return false;
      }
      return 1500; // Poll every 1.5 seconds
    },
    refetchIntervalInBackground: true,
    staleTime: 0, // Always consider data stale to ensure fresh polling,
  });

  // When all the files are uploaded and the batch is processed (i.e. empty)...
  useEffect(() => {
    if (hasUploadedFiles && uploadBatchesQuery.data?.length === 0) {
      // Clear the uploadBatchesQuery cache so this conditional works the next time we upload.
      queryClient.removeQueries({
        queryKey: uploadBatchesQueryKey,
      });

      // Optimistically update the case files query with the new file.
      queryClient.setQueryData(
        caseFilesQueryOptions.queryKey,
        (oldData: CaseFilePage | undefined) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            items: [
              {
                id: uploadFileMutation.data?.id ?? "",
                userId: uploadFileMutation.data?.userId ?? "",
                name: uploadFileMutation.data?.files?.[0]?.name ?? "",
                source: Source.Uploaded,
                status: DocumentStatus.Uploaded,
                uploadedByClient: true,
                isWithheld: false,
                isPrivileged: false,
                type: CaseFileType.Pleading,
              },
            ],
          };
        },
      );

      // Stop polling the batch and reveal the new file.
      setHasUploadedFiles(false);

      // Set the complaint step to complete
      props.setIsComplete(true);

      // Refetch the case files query
      queryClient.invalidateQueries({
        queryKey: caseFilesQueryOptions.queryKey,
      });
    }
  }, [
    uploadBatchesQuery.data,
    hasUploadedFiles,
    uploadBatchesQueryKey,
    queryClient,
    uploadFileMutation.data,
    props.setIsComplete,
    caseFilesQueryOptions.queryKey,
  ]);

  const deleteFileMutation = useMutation({
    mutationFn: async (file: CaseFileLite) => {
      return caseFileApi.caseFileDelete({
        caseId: caseIdParam,
        fileId: file.id,
      });
    },
    onMutate: async () => {
      // Cancel any outgoing refetch so that we don't overwrite any other changes
      await queryClient.cancelQueries({
        queryKey: caseFilesQueryOptions.queryKey,
      });

      // Snapshot the previous value of the query data
      const previousData = queryClient.getQueryData(
        caseFilesQueryOptions.queryKey,
      );

      // Optimistically update case files query to remove the file
      queryClient.setQueryData(
        caseFilesQueryOptions.queryKey,
        (oldData: CaseFilePage | undefined) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            items: [],
          };
        },
      );

      return { previousData };
    },
    onSuccess: (_, file) => {
      toast.success(`${file.name} was removed`);
    },
    onError: (error, __, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          caseFilesQueryOptions.queryKey,
          context.previousData,
        );
      }
      rg4js("send", {
        error: normalizeError(error),
      });
      toast.error("Unable to delete complaint file.", {
        description: "Error:",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: caseFilesQueryOptions.queryKey,
      });
    },
  });

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    e.preventDefault();
    const files = e.target.files;
    const sanitizedFiles = sanitizeConvertableFiles(
      files ? Array.from(files) : [],
    );

    // Clear the input immediately after processing
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    // check if any file is pdf
    if (sanitizedFiles?.length) {
      uploadFileMutation.mutate(sanitizedFiles);
    } else {
      toast.error("Please upload a PDF file");
    }
  }

  const uploadComplaintText =
    uploadFileMutation.isPending || hasUploadedFiles
      ? "Uploading a complaint..."
      : "Upload complaint";

  const isUploading = uploadFileMutation.isPending || hasUploadedFiles;

  return (
    <>
      <LeavingGuard
        when={isUploading}
        message="Your upload will stop if you leave this page."
        title="Upload in progress!"
      />{" "}
      <div className="d-flex">
        <StepIndicator
          stepNumber={1}
          isWarning={props.hasWarning}
          isSuccess={props.isComplete}
          isLoading={isUploading}
        />
        <div className={styles.container}>
          <div
            className={clsx(
              styles.box,
              props.isComplete && styles.successBox,
              props.hasWarning && styles.warningBox,
            )}
          >
            <Loading
              isLoading={caseFilesQuery.isLoading}
              align="left"
              className={styles.loading}
            >
              <div
                data-testid="complaint-step-contents"
                className={`${styles.contents} d-flex justify-content-between align-items-center`}
              >
                <div
                  className={styles.title}
                  data-testid="complaint-step-title"
                >
                  {props.isComplete ? (
                    <div>
                      {caseFilesQuery.data?.name && (
                        <>
                          <span className="fw-bold">
                            {caseFilesQuery.data?.name}
                          </span>
                          &nbsp;uploaded
                        </>
                      )}
                    </div>
                  ) : (
                    uploadComplaintText
                  )}
                  {!isUploading && !props.isComplete && (
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip>
                          <div className="text-center">
                            Upload any document that summarizes your case. We
                            recommend a complaint.
                          </div>
                        </Tooltip>
                      }
                    >
                      <InfoCircle className="ms-1" />
                    </OverlayTrigger>
                  )}
                  {props.hasWarning && (
                    <div className={styles.warningText}>
                      Required to proceed to Review
                    </div>
                  )}
                </div>
                <form>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept="application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword"
                    data-testid="complaint-file-input"
                    hidden
                  />
                  {!isUploading && !props.isComplete ? (
                    <Button
                      size="sm"
                      className={styles.uploadButton}
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      data-testid="upload-complaint-button"
                    >
                      Upload{" "}
                      {
                        getSubTypeText(
                          DocumentSubType.Complaint,
                          caseQuery.data?.jurisdiction ?? "",
                        ).singular
                      }
                    </Button>
                  ) : (
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip data-testid="remove-complaint-tooltip">
                          <div className="text-center">Remove</div>
                        </Tooltip>
                      }
                    >
                      <CloseButton
                        data-testid="remove-complaint-button"
                        className={`${styles.closeButton} me-1`}
                        onClick={() =>
                          caseFilesQuery.data &&
                          deleteFileMutation.mutate(caseFilesQuery.data)
                        }
                        disabled={
                          deleteFileMutation.isPending ||
                          caseFilesQuery.isFetching
                        }
                      />
                    </OverlayTrigger>
                  )}
                </form>
              </div>
            </Loading>
            {isUploading && (
              <div
                className={styles.loaderBar}
                style={{ width: `${estimatedProgress}%` }}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}
